/**
 * RevenueCat Client Utilities
 *
 * Client-side utilities for interacting with RevenueCat SDK
 */
import type { AttributionData } from "@/lib/analytics/utm-tracker";
import { createLogger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import { LogLevel, Purchases } from "@revenuecat/purchases-js";
import { isRevenueCatAnonymousAppUserId, normalizeRevenueCatAppUserId } from "./app-user-id";
import { REVENUECAT_CONFIG } from "./config";
import { normalizeRevenueCatPurchaseError } from "./purchase-errors";

const logger = createLogger("RevenueCatClient");

let purchasesInstance: Purchases | null = null;
let anonymousAppUserId: string | null = null;

export const PURCHASE_STALL_WARNING_MS = 60_000;

export type PurchaseWatchdogOptions = {
  stallWarningMs?: number;
  onStalled?: () => void;
};

/**
 * Initialize RevenueCat SDK
 * Should be called once when the app loads
 *
 * @param appUserId - The user's email address (required)
 */
export async function initializeRevenueCat(appUserId: string): Promise<Purchases> {
  const normalizedId = normalizeRevenueCatAppUserId(appUserId);
  if (!normalizedId) {
    throw new Error("appUserId is required for RevenueCat initialization");
  }

  if (purchasesInstance) {
    const currentId = await purchasesInstance.getAppUserId();
    if (currentId !== normalizedId) {
      logger.info("Switching RevenueCat user", { from: currentId, to: normalizedId });
      const apiKey = REVENUECAT_CONFIG.publicApiKey;
      purchasesInstance = await Purchases.configure(apiKey, normalizedId);
    }
    return purchasesInstance;
  }

  try {
    const apiKey = REVENUECAT_CONFIG.publicApiKey;

    if (!apiKey) {
      throw new Error("RevenueCat public API key not configured");
    }

    // Email IDs are normalized; generated anonymous IDs retain their exact prefix.
    purchasesInstance = await Purchases.configure(apiKey, normalizedId);

    // Set log level based on environment
    if (process.env.NODE_ENV === "development") {
      Purchases.setLogLevel(LogLevel.Debug);
    }

    logger.info("RevenueCat initialized", { appUserId });

    return purchasesInstance;
  } catch (error) {
    logger.error("Failed to initialize RevenueCat", { error });
    throw error;
  }
}

export async function initializeRevenueCatAnonymous(
  existingAnonymousAppUserId?: string | null
): Promise<Purchases> {
  const appUserId =
    existingAnonymousAppUserId && isRevenueCatAnonymousAppUserId(existingAnonymousAppUserId)
      ? existingAnonymousAppUserId
      : anonymousAppUserId || Purchases.generateRevenueCatAnonymousAppUserId();
  anonymousAppUserId = appUserId;
  return initializeRevenueCat(appUserId);
}

export async function getRevenueCatAppUserId(): Promise<string | null> {
  return purchasesInstance ? purchasesInstance.getAppUserId() : null;
}

export async function identifyRevenueCatUser(appUserId: string) {
  const normalizedId = normalizeRevenueCatAppUserId(appUserId);
  if (!normalizedId) throw new Error("appUserId is required for RevenueCat identification");

  const purchases = getRevenueCat();
  if (!purchases) throw new Error("RevenueCat not initialized");

  const currentId = await purchases.getAppUserId();
  if (currentId === normalizedId) return purchases.getCustomerInfo();
  if (isRevenueCatAnonymousAppUserId(currentId)) {
    const result = await purchases.identifyUser(normalizedId);
    anonymousAppUserId = null;
    return result.customerInfo;
  }

  return initializeRevenueCat(normalizedId).then((instance) => instance.getCustomerInfo());
}

/**
 * Get current RevenueCat instance
 */
export function getRevenueCat(): Purchases | null {
  return purchasesInstance;
}

/**
 * Get available offerings from RevenueCat
 */
export async function getOfferings() {
  try {
    const purchases = getRevenueCat();
    if (!purchases) {
      throw new Error("RevenueCat not initialized");
    }

    const offerings = await purchases.getOfferings();
    logger.info("Fetched offerings", {
      current: offerings.current?.identifier,
      availableCount: offerings.all ? Object.keys(offerings.all).length : 0,
    });

    return offerings;
  } catch (error) {
    logger.error("Failed to fetch offerings", { error });
    throw error;
  }
}

/**
 * Get customer info (subscription status)
 */
export async function getCustomerInfo() {
  try {
    const purchases = getRevenueCat();
    if (!purchases) {
      throw new Error("RevenueCat not initialized");
    }

    const customerInfo = await purchases.getCustomerInfo();
    logger.info("Fetched customer info", {
      activeSubscriptions: customerInfo.activeSubscriptions,
      entitlements: Object.keys(customerInfo.entitlements.active),
    });

    return customerInfo;
  } catch (error) {
    logger.error("Failed to fetch customer info", { error });
    throw error;
  }
}

/**
 * Purchase a package
 */
export async function purchasePackage(
  packageToPurchase: any,
  metadata?: Record<string, string>,
  customerEmail?: string,
  watchdog?: PurchaseWatchdogOptions
) {
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  try {
    const purchases = getRevenueCat();
    if (!purchases) {
      throw new Error("RevenueCat not initialized");
    }

    logger.info("Initiating purchase", {
      packageId: packageToPurchase.identifier,
      productIdentifier: packageToPurchase?.product?.identifier,
      productPrice: packageToPurchase?.product?.price,
      productPriceString: packageToPurchase?.product?.priceString,
      storeProduct: packageToPurchase?.product,
      metadata,
      customerEmail: customerEmail ? "provided" : "not provided",
    });

    const purchaseOptions: any = {
      rcPackage: packageToPurchase,
    };

    // Add metadata if provided
    if (metadata) {
      purchaseOptions.metadata = metadata;
    }

    // Pre-fill customer email to skip email collection step (faster checkout)
    if (customerEmail) {
      purchaseOptions.customerEmail = customerEmail;
    }

    if (watchdog?.onStalled) {
      stallTimer = setTimeout(() => {
        try {
          watchdog.onStalled?.();
        } catch (error) {
          logger.warn("Purchase stall callback failed", { error });
        }
      }, watchdog.stallWarningMs ?? PURCHASE_STALL_WARNING_MS);
    }

    const result = await purchases.purchase(purchaseOptions);

    logger.info("Purchase successful", {
      activeSubscriptions: result.customerInfo.activeSubscriptions,
      metadata,
    });

    return result;
  } catch (error: unknown) {
    const normalizedError = normalizeRevenueCatPurchaseError(error);
    if (normalizedError.isCancelled) {
      logger.info("User cancelled purchase");
    } else {
      logger.error("Purchase failed", {
        errorCategory: normalizedError.category,
        errorCode: normalizedError.errorCode,
        statusCode: normalizedError.statusCode,
        backendErrorCode: normalizedError.backendErrorCode,
      });
    }

    throw error;
  } finally {
    if (stallTimer) clearTimeout(stallTimer);
  }
}

/**
 * Restore purchases
 */
export async function restorePurchases() {
  try {
    const purchases = getRevenueCat();
    if (!purchases) {
      throw new Error("RevenueCat not initialized");
    }

    logger.info("Restoring purchases");
    const customerInfo =
      typeof (purchases as any).restorePurchases === "function"
        ? await (purchases as any).restorePurchases()
        : await purchases.getCustomerInfo();

    logger.info("Purchases restored", {
      activeSubscriptions: customerInfo.activeSubscriptions,
      entitlements: Object.keys(customerInfo.entitlements?.active ?? {}),
    });

    return customerInfo;
  } catch (error) {
    logger.error("Failed to restore purchases", { error });
    throw error;
  }
}

/**
 * Check if user has active subscription
 */
export function hasActiveSubscription(customerInfo: any): boolean {
  const activeSubscriptions = customerInfo?.activeSubscriptions;
  if (activeSubscriptions instanceof Set) return activeSubscriptions.size > 0;
  if (Array.isArray(activeSubscriptions)) return activeSubscriptions.length > 0;
  return false;
}

export function hasActiveRevenueCatAccess(customerInfo: any): boolean {
  return (
    hasActiveSubscription(customerInfo) ||
    Object.keys(customerInfo?.entitlements?.active ?? {}).length > 0
  );
}

/**
 * Get active entitlements
 */
export function getActiveEntitlements(customerInfo: any): string[] {
  return Object.keys(customerInfo.entitlements.active);
}

/**
 * Set attribution data for RevenueCat subscriber
 * This can be called to update attribution fields for existing subscribers
 *
 * @param appUserId - The RevenueCat app user ID
 * @param attribution - Attribution data to set
 */
export async function setAttributionData(
  appUserId: string,
  attribution: AttributionData
): Promise<void> {
  try {
    if (!appUserId) {
      throw new Error("appUserId is required for setting attribution");
    }

    if (!attribution) {
      logger.warn("No attribution data provided");
      return;
    }

    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session?.access_token) {
      throw new Error(sessionError?.message || "Authenticated session is required");
    }

    // Call the sync attribution endpoint
    const response = await fetch("/api/revenuecat/sync-attribution", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        appUserId,
        attribution,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to set attribution: ${errorText}`);
    }

    const result = (await response.json()) as any;

    if (!result.skipped) {
      logger.info("Attribution data set successfully", {
        appUserId,
        network: attribution.network,
        campaign: attribution.utm_campaign,
        adGroup: attribution.ad_group,
      });
    } else {
      logger.info("Attribution data sync skipped (subscriber not found yet)");
    }
  } catch (error) {
    logger.error("Failed to set attribution data", { error });
    throw error;
  }
}

/**
 * Set custom attributes for the user
 * These are synced with RevenueCat and can be used for targeting or analysis
 *
 * @param attributes - Key-value pairs of attributes to set
 */
export async function setAttributes(attributes: Record<string, string | null>) {
  try {
    const purchases = getRevenueCat();
    if (!purchases) {
      throw new Error("RevenueCat not initialized");
    }

    logger.info("Setting user attributes", { keys: Object.keys(attributes) });

    // Cast to any to avoid type issues if types are outdated, but setAttributes is standard in SDK
    await (purchases as any).setAttributes(attributes);

    logger.info("User attributes set successfully");
    return true;
  } catch (error) {
    const normalizedError = normalizeRevenueCatPurchaseError(error);
    const diagnostic = {
      keys: Object.keys(attributes),
      errorCategory: normalizedError.category,
      errorCode: normalizedError.errorCode,
      backendErrorCode: normalizedError.backendErrorCode,
    };

    if (normalizedError.category === "invalid_subscriber_attributes") {
      logger.warn("RevenueCat rejected one or more non-critical user attributes", diagnostic);
    } else {
      logger.error("Failed to set user attributes", diagnostic);
    }
    // Don't throw here to avoid blocking main flows if attribute setting fails
    return false;
  }
}

/**
 * Set email attribute for the user
 */
export async function setEmailAttribute(email: string) {
  return setAttributes({ $email: email });
}

/**
 * Get attribution summary for display
 */
export function getAttributionSummary(attribution: AttributionData | null): string {
  if (!attribution) {
    return "No attribution data";
  }

  const parts: string[] = [];

  if (attribution.network) {
    parts.push(`Network: ${attribution.network}`);
  }

  if (attribution.utm_campaign) {
    parts.push(`Campaign: ${attribution.utm_campaign}`);
  }

  if (attribution.ad_group) {
    parts.push(`Ad Group: ${attribution.ad_group}`);
  }

  if (attribution.keywords) {
    parts.push(`Keywords: ${attribution.keywords}`);
  }

  if (attribution.creative) {
    parts.push(`Creative: ${attribution.creative}`);
  }

  return parts.length > 0 ? parts.join(" | ") : "Basic UTM attribution only";
}
